'use client';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MarkdownPreviewProps } from 'elowen-plugin-ui-kit';
import { MENTION_SLOT, markMentions, mentionKey } from 'elowen-plugin-shared/composerMentions';
import { markdownDocument } from '../../lib/markdown';
import { MentionChip, useMentionTokens } from './MentionChip';

const NO_SLOTS: readonly HTMLElement[] = [];

/** Authored Markdown through the host sanitizer; the caller owns padding, height and scrolling.
 *
 *  With `mentions` it draws the chat's chips in the prose and nowhere else: `markMentions` marks only whole
 *  tokens that start a word and skips `code`, `pre` and links (so a pasted snippet or an autolinked address
 *  stays quoted text), and each marked slot receives the shared `MentionChip` through a portal before paint.
 *  The person, the word a screen reader hears and the agent's mark all arrive as props. Without `mentions`
 *  the document renders exactly as before and nothing extra is scanned. */
export function MarkdownPreview({ source, className = '', mentions, people, viewerId, prefix, agentSrc }: MarkdownPreviewProps) {
  const tokens = useMentionTokens(mentions, people);
  const html = useMemo(() => {
    const document = markdownDocument(source);
    markMentions(document, tokens);
    return document.innerHTML;
  }, [source, tokens]);
  const root = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState<readonly HTMLElement[]>(NO_SLOTS);
  // New HTML means new slot elements; they are collected once the document is in the DOM, before paint.
  useLayoutEffect(() => {
    const found = tokens.length ? [...root.current!.querySelectorAll<HTMLElement>(`[${MENTION_SLOT}]`)] : NO_SLOTS;
    if (found.length !== slots.length || found.some((slot, i) => slot !== slots[i])) setSlots(found);
  }, [html, tokens.length, slots]);
  const classes = `markdown-preview inline-links text-body text-foreground ${className}`;
  const chips = slots.map((slot, i) => {
    const token = tokens.find(candidate => mentionKey(candidate) === slot.getAttribute(MENTION_SLOT) && candidate.label === slot.dataset.mentionLabel);
    return token ? createPortal(<MentionChip token={token} viewerId={viewerId} prefix={prefix} agentSrc={agentSrc} />, slot, String(i)) : null;
  });
  // A portal is a child of the frame, so the document itself sits in one `contents` box: its markup keeps the
  // flow and every `.markdown-preview` rule, while the chips still have a React place in the tree.
  if (!tokens.length) return <div ref={root} className={classes} dangerouslySetInnerHTML={{ __html: html }} />;
  return (
    <div ref={root} className={classes}>
      <div className="contents" dangerouslySetInnerHTML={{ __html: html }} />
      {chips}
    </div>
  );
}
